import React, { useEffect, useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Search, ShieldCheck, ShieldX, KeyRound, Clock, Mail, RefreshCw,
  Wand2, Hourglass, Check, X, Loader2, AlertTriangle,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";

const TABS = [
  { id: "verify", label: "Pending Verifications", icon: <KeyRound className="w-4 h-4" /> },
  { id: "reset", label: "Pending Password Resets", icon: <Wand2 className="w-4 h-4" /> },
];

// Tiny debounce so typing in the search field doesn't hammer the API.
function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function AdminManualVerify() {
  const [tab, setTab] = useState("verify");
  const [q, setQ] = useState("");
  const qd = useDebounced(q, 300);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("pending");

  // Per-row "currently submitting" set — blocks double-clicks AND grays the row.
  const [pendingActions, setPendingActions] = useState(() => new Set());
  // Confirmation dialog state. `null` ⇒ closed.
  const [confirm, setConfirm] = useState(null);

  // Avoid stale-closure setState after the component unmounts.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "verify") {
        const { data } = await api.get("/admin/manual-verify/pending", { params: { q: qd, limit: 100 } });
        setItems(data.items || []);
        setTotal(data.total || 0);
      } else {
        const { data } = await api.get("/admin/manual-resets/pending", { params: { q: qd, status, limit: 100 } });
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  }, [tab, qd, status]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const markPending = (key, on) => {
    setPendingActions((prev) => {
      const next = new Set(prev);
      if (on) next.add(key); else next.delete(key);
      return next;
    });
  };

  // ---- Approve / Reject verification ----
  const askVerify = (row, action) => {
    setConfirm({
      kind: "verify",
      action,
      row,
      title: action === "approve" ? "Approve account?" : "Reject account?",
      message: action === "approve"
        ? `Verify ${row.email} (${row.verification_code}). They will be able to log in immediately.`
        : `Reject ${row.email} (${row.verification_code}). The account will be suspended.`,
      tone: action === "approve" ? "primary" : "danger",
      needsNote: action === "reject",
      noteLabel: "Reason (optional)",
      confirmLabel: action === "approve" ? "Approve" : "Reject",
    });
  };

  const doVerify = async (row, action, admin_note) => {
    const key = `verify:${row.id}`;
    markPending(key, true);
    try {
      await api.post("/admin/manual-verify", {
        user_id: row.id,
        code: row.verification_code,
        action,
        admin_note: admin_note || "",
      });
      // Optimistic — remove from local list immediately, no refetch.
      if (mounted.current) {
        setItems((prev) => prev.filter((x) => x.id !== row.id));
        setTotal((prev) => Math.max(0, prev - 1));
      }
      toast.success(action === "approve" ? "Account verified." : "Account rejected.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
      // On failure, refresh to reconcile state.
      fetchData();
    } finally {
      if (mounted.current) markPending(key, false);
    }
  };

  // ---- Decide reset ----
  const askReset = (row, action) => {
    const labels = {
      approve:       "Approve reset?",
      reject:        "Reject reset?",
      set_temporary: "Set temporary password?",
      force_change:  "Approve & force password change?",
    };
    const msgs = {
      approve:       `Approve reset for ${row.user_email}. They can complete the reset with code ${row.code}.`,
      reject:        `Reject reset for ${row.user_email}. The pending request will be marked rejected.`,
      set_temporary: `Set a temporary password for ${row.user_email}. They will be forced to change it on next login.`,
      force_change:  `Approve reset for ${row.user_email} AND force them to change password on next login.`,
    };
    setConfirm({
      kind: "reset",
      action,
      row,
      title: labels[action] || "Confirm",
      message: msgs[action],
      tone: action === "reject" ? "danger" : action === "approve" ? "primary" : "warning",
      needsNote: action === "reject",
      noteLabel: "Reason (optional)",
      confirmLabel: { approve: "Approve", reject: "Reject", set_temporary: "Set Temp", force_change: "Force Change" }[action],
      needsPassword: action === "set_temporary",
    });
  };

  const doReset = async (row, action, admin_note, temporary_password) => {
    const key = `reset:${row.id}`;
    markPending(key, true);
    try {
      await api.post("/admin/manual-resets/decide", {
        request_id: row.id,
        action,
        admin_note: admin_note || "",
        temporary_password: temporary_password || null,
      });
      // Optimistically remove from the list ONLY when the user is viewing
      // status=pending (the row no longer matches the filter). For other
      // status filters (approved, completed, all), refetch to reflect the
      // new status accurately.
      if (mounted.current) {
        if (status === "pending") {
          setItems((prev) => prev.filter((x) => x.id !== row.id));
          setTotal((prev) => Math.max(0, prev - 1));
        } else {
          fetchData();
        }
      }
      const msg = {
        approve:       "Reset approved. User can now set a new password using their code.",
        reject:        "Reset rejected.",
        set_temporary: "Temporary password set. User must change on next login.",
        force_change:  "Reset approved. User will be forced to change password on next login.",
      }[action];
      toast.success(msg);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
      fetchData();
    } finally {
      if (mounted.current) markPending(key, false);
    }
  };

  const onConfirm = async ({ note, password }) => {
    const c = confirm;
    setConfirm(null);
    if (!c) return;
    if (c.kind === "verify") {
      await doVerify(c.row, c.action, note);
    } else {
      await doReset(c.row, c.action, note, password);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mystic text-rose-400 text-xs">手動承認</div>
          <h2 className="font-display text-2xl font-bold text-white">Manual Verification</h2>
          <p className="text-zinc-400 text-xs mt-1">Approve email verification &amp; password reset requests when email delivery fails.</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} className="border-white/15 text-zinc-300" data-testid="manual-verify-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="flex items-center gap-2 border-b border-white/10">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => { setTab(t.id); setQ(""); }}
            data-testid={`manual-verify-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-semibold flex items-center gap-1.5 -mb-px border-b-2 ${
              tab === t.id ? "border-rose-500 text-white" : "border-transparent text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by code, email, or username…"
            className="pl-10 bg-black/40 border-white/10 text-white"
            data-testid="manual-verify-search"
          />
        </div>
        {tab === "reset" && (
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2 rounded-md bg-black/40 border border-white/10 text-zinc-200 text-sm"
            data-testid="manual-verify-status"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved (awaiting user)</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </select>
        )}
        <span className="text-xs text-zinc-500">{total} result{total === 1 ? "" : "s"}</span>
      </div>

      <div className="glass rounded-xl border border-white/10 overflow-hidden">
        {loading && (
          <div className="px-4 py-3 text-xs text-zinc-500 italic">Loading…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="px-4 py-10 text-center">
            <Hourglass className="w-8 h-8 mx-auto text-zinc-600 mb-2" />
            <div className="text-zinc-400 text-sm">No {tab === "verify" ? "pending verifications" : "reset requests"}.</div>
          </div>
        )}
        {!loading && items.length > 0 && tab === "verify" && (
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-zinc-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Code</th>
                <th className="text-left px-4 py-2">User</th>
                <th className="text-left px-4 py-2">Issued</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => {
                const pendingKey = `verify:${u.id}`;
                const busy = pendingActions.has(pendingKey);
                return (
                  <tr key={u.id} className={`border-t border-white/5 hover:bg-white/[0.02] ${busy ? "opacity-50" : ""}`}>
                    <td className="px-4 py-3">
                      <code className="bg-black/40 border border-rose-500/20 px-2 py-1 rounded text-rose-300 font-mono">
                        {u.verification_code || "—"}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-zinc-100 font-medium">{u.name}</div>
                      <div className="text-xs text-zinc-500 flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-400">
                      {u.verification_code_issued_at ? new Date(u.verification_code_issued_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <Button size="sm" onClick={() => askVerify(u, "approve")} disabled={busy} className="bg-emerald-600/80 hover:bg-emerald-500 text-white" data-testid={`verify-approve-${u.id}`}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><ShieldCheck className="w-3.5 h-3.5 mr-1" /> Approve</>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => askVerify(u, "reject")} disabled={busy} className="border-red-500/40 text-red-300 hover:bg-red-950/40" data-testid={`verify-reject-${u.id}`}>
                        <ShieldX className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!loading && items.length > 0 && tab === "reset" && (
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-zinc-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Code</th>
                <th className="text-left px-4 py-2">User</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Requested</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const isPending = r.status === "pending";
                const isApproved = r.status === "approved";
                const pendingKey = `reset:${r.id}`;
                const busy = pendingActions.has(pendingKey);
                return (
                  <tr key={r.id} className={`border-t border-white/5 hover:bg-white/[0.02] ${busy ? "opacity-50" : ""}`}>
                    <td className="px-4 py-3">
                      <code className="bg-black/40 border border-amber-500/20 px-2 py-1 rounded text-amber-300 font-mono">
                        {r.code}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-zinc-100 font-medium">{r.user_name || "—"}</div>
                      <div className="text-xs text-zinc-500 flex items-center gap-1"><Mail className="w-3 h-3" />{r.user_email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill status={r.status} />
                      {r.force_change_on_next_login && (
                        <div className="text-[10px] text-amber-300 mt-1">force change on next login</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-400">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right space-x-1">
                      {(isPending || isApproved) && (
                        <>
                          {isPending && (
                            <Button size="sm" onClick={() => askReset(r, "approve")} disabled={busy} className="bg-emerald-600/80 hover:bg-emerald-500 text-white" data-testid={`reset-approve-${r.id}`}>
                              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5 mr-1" /> Approve</>}
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => askReset(r, "set_temporary")} disabled={busy} className="border-amber-500/40 text-amber-300 hover:bg-amber-950/40" data-testid={`reset-set-temp-${r.id}`}>
                            <KeyRound className="w-3.5 h-3.5 mr-1" /> Set Temp
                          </Button>
                          {isPending && (
                            <Button size="sm" variant="outline" onClick={() => askReset(r, "force_change")} disabled={busy} className="border-sky-500/40 text-sky-300 hover:bg-sky-950/40" data-testid={`reset-force-${r.id}`}>
                              <Clock className="w-3.5 h-3.5 mr-1" /> Force Change
                            </Button>
                          )}
                          {isPending && (
                            <Button size="sm" variant="outline" onClick={() => askReset(r, "reject")} disabled={busy} className="border-red-500/40 text-red-300 hover:bg-red-950/40" data-testid={`reset-reject-${r.id}`}>
                              <X className="w-3.5 h-3.5 mr-1" /> Reject
                            </Button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        tone={confirm?.tone}
        needsNote={confirm?.needsNote}
        noteLabel={confirm?.noteLabel}
        needsPassword={confirm?.needsPassword}
        confirmLabel={confirm?.confirmLabel}
        onCancel={() => setConfirm(null)}
        onConfirm={onConfirm}
      />
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending:   { txt: "Pending",   cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
    approved:  { txt: "Approved",  cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
    completed: { txt: "Completed", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
    rejected:  { txt: "Rejected",  cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  };
  const m = map[status] || { txt: status, cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${m.cls}`}>{m.txt}</span>;
}
